import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, X, Filter } from "lucide-react";
import { useState } from "react";
import { Badge } from "./ui/badge";

export interface EnhancedSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  suggestions?: string[];
  filters?: { label: string; value: string; active: boolean }[];
  onFilterChange?: (filterValue: string) => void;
  resultsCount?: number;
  onSuggestionClick?: (suggestion: string) => void;
}

export function EnhancedSearch({ 
  value, 
  onChange, 
  placeholder, 
  suggestions = [],
  filters = [],
  onFilterChange,
  resultsCount,
  onSuggestionClick
}: EnhancedSearchProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleClear = () => {
    onChange('');
    setIsFocused(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setIsFocused(false);
    onSuggestionClick?.(suggestion);
  };

  const showSuggestions = isFocused && value.length > 0 && suggestions.length > 0;

  return (
    <div className="w-full max-w-2xl mx-auto mb-6 space-y-4">
      <div className="relative focus-within:z-[1000]">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <Input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          className="pl-10 pr-10"
        />
        {value && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        
        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-[1000] max-h-60 overflow-y-auto ring-1 ring-black/5">
            {suggestions.slice(0, 8).map((suggestion, index) => (
              <button
                key={index}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <span className="text-sm">{suggestion}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      {filters.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-600">Filter:</span>
          {filters.map((filter) => (
            <Badge
              key={filter.value}
              variant={filter.active ? "default" : "outline"}
              className="cursor-pointer transition active:scale-95 focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none"
              onClick={() => onFilterChange?.(filter.value)}
              role="button"
              aria-pressed={filter.active}
              title={filter.label}
            >
              {filter.label}
            </Badge>
          ))}
        </div>
      )}

      {/* Results Count */}
      {resultsCount !== undefined && (
        <div className="text-sm text-gray-600 text-center">
          {resultsCount === 0 ? 'No results found' : `${resultsCount} result${resultsCount === 1 ? '' : 's'} found`}
        </div>
      )}
    </div>
  );
}